import classNames from 'classnames';
import ReactGA from 'react-ga4';
import { useHistory } from 'react-router-dom';

import { Caseload, Homeless, CareCoordinatorReferral } from 'types';

import ReferralTag from 'components/_shared/ReferralTag';

import { capitalizeWord } from 'util/index';

import { ButtonBase, CardMedia, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles(() =>
    createStyles({
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '16px',
            cursor: 'pointer',
            height: '100%',
            '&:focus-visible': {
                outline: '5px solid #0066cc',
                outlineOffset: '2px'
            }
        },
        cardContainer: {
            backgroundColor: 'rgb(137,157,193)',
            borderRadius: '16px',
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            '& img': {
                height: '100%',
                objectFit: 'cover',
                objectPosition: '50% 30%',
                width: '100%'
            }
        },
        holderName: {
            bottom: '12px',
            left: '16px',
            color: '#FFFFFF',
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: '24px',
            position: 'absolute',
            wordBreak: 'break-word'
        },
        nameContainer: {
            position: 'relative',
            bottom: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        overlay: {
            position: 'absolute',
            backgroundColor: 'rgba(0,0,0,0.4)',
            borderRadius: '16px',
            height: '100%',
            width: '100%'
        },
        overlayDark: {
            backgroundColor: 'rgba(0,0,0,0.7)'
        }
    })
);

type HolderListItemProps = {
    caseload: Caseload;
    holder: Homeless;
    memberCareCoordinatorReferrals: CareCoordinatorReferral[] | null;
    setReferCaseload: (value: Caseload) => void;
    setReferHolder: (value: Homeless) => void;
    setReferPopup: (value: boolean) => void;
};

const HoldersListItem: React.FC<HolderListItemProps> = ({
    caseload,
    holder,
    memberCareCoordinatorReferrals,
    setReferCaseload,
    setReferHolder,
    setReferPopup
}) => {
    const classes = useStyles();
    const history = useHistory();

    const { status = 'active', is_accepted } = caseload || {};
    const { mobile_photo, profile_stage } = holder;

    const overlayClass = classNames(classes.overlay, {
        [classes.overlayDark]:
            status === 'pending' ||
            profile_stage === 'MEMBERSHIP_CONFIRMED' ||
            profile_stage === 'PENDING'
    });

    const handleClick = () => {
        ReactGA.event({
            category: 'Holder',
            action: 'View Holder',
            label: holder.id.toString()
        });

        if (status === 'pending') {
            setReferCaseload(caseload);
            setReferHolder(holder);
            setReferPopup(true);
        } else {
            history.push(`/holders/${holder.id}`);
        }
    };

    const renderReferralTagText = () => {
        if (typeof is_accepted !== 'undefined') {
            return is_accepted;
        }

        if (profile_stage === 'MEMBERSHIP_CONFIRMED') {
            return 'membership confirmed';
        }

        if (profile_stage === 'PENDING') {
            // Check if user is care coordinator and has active referrals for this holder
            if (memberCareCoordinatorReferrals) {
                const activeReferrals = memberCareCoordinatorReferrals.filter(
                    (referral: CareCoordinatorReferral) => referral.is_active
                );
                
                if (activeReferrals.length > 0) {
                    // Check if any referral has empty ctp_caseload_referrals
                    const hasEmptyCtpReferrals = activeReferrals.some(
                        (referral: CareCoordinatorReferral) => referral.ctp_caseload_referrals.length === 0
                    );
                    
                    if (hasEmptyCtpReferrals) {
                        return 'pending: referral required';
                    }
                }
            }
            return 'pending';
        }

        if (profile_stage === 'UNSUCCESSFUL_PENDING') {
            return 'pending membership ended';
        }

        if (profile_stage === 'EXITED') {
            return 'unenrolled';
        }

        return 'referred to you';
    };

    const referralTagText = renderReferralTagText();
    const memberName = capitalizeWord(holder.name);
    
    // Build accessible label: name, referral tag (if shown), and link purpose
    const shouldShowTag = status === 'pending' ||
        (status === 'active' && is_accepted) ||
        (status === 'cancelled' && is_accepted) ||
        profile_stage === 'MEMBERSHIP_CONFIRMED' ||
        profile_stage === 'PENDING' ||
        profile_stage === 'UNSUCCESSFUL_PENDING' ||
        profile_stage === 'EXITED';
    
    const ariaLabel = shouldShowTag
        ? `${memberName}, ${referralTagText}, go to member's profile`
        : `${memberName}, go to member's profile`;

    return (
        <ButtonBase
            className={classes.content}
            component="div"
            onClick={handleClick}
            aria-label={ariaLabel}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick();
                }
            }}
        >
            <div className={classes.cardContainer}>
                {shouldShowTag && (
                    <span aria-hidden="true">
                        <ReferralTag text={referralTagText} />
                    </span>
                )}
                <span className={overlayClass} aria-hidden="true" />
                <CardMedia
                    component="img"
                    image={mobile_photo}
                    loading="lazy"
                    alt={`${memberName}'s profile picture`}
                    aria-hidden="true"
                />
                <div className={classes.nameContainer} aria-hidden="true">
                    <Typography className={classes.holderName}>
                        {memberName}
                    </Typography>
                </div>
            </div>
        </ButtonBase>
    );
};

export default HoldersListItem;
